import { Clock } from "lucide-react"
import { useTranslation } from "react-i18next"

import { MOTION_BEATS } from "@/lib/motion"
import { cn } from "@/lib/utils"
import type { CrewDrop } from "@/types/collab"

interface MissionDropCardProps {
  drop?: CrewDrop | null
  className?: string
}

/**
 * Civil day difference (drop day − today), timezone-safe. Used to drive a
 * localized relative-time string ("today" / "tomorrow" / "in 2 days").
 */
function dayDelta(dropsAt: string, now: Date): number | null {
  const drop = new Date(dropsAt)
  if (Number.isNaN(drop.getTime())) return null
  const civil = (d: Date) =>
    Math.floor(
      Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000,
    )
  return civil(drop) - civil(now)
}

/**
 * OKS-472 — New Drop countdown card (Figma `35:153`). An accent-tinted card
 * teasing the nearest scheduled assignment reveal: "NEW DROP" eyebrow +
 * "{label} — {N} missions land {when}" + clock icon.
 *
 * Informational only (PRD §3) — NOT a marketplace/claim surface; tapping does
 * nothing until the drop lands (the reveal job is OKS-473's, TRD KD-2).
 * Renders NOTHING when `drop` is null/absent (no upcoming drop). Plays a
 * one-time CSS reveal on mount (reduced-motion-safe).
 *
 * Brand isolation: `drop` is resolved server-side from `Mission.for_brand`
 * (active brand only) — this component renders text only.
 */
export function MissionDropCard({ drop, className }: MissionDropCardProps) {
  const { t, i18n } = useTranslation()

  if (!drop) return null

  const delta = dayDelta(drop.drops_at, new Date())
  // Not formatRelativeIntl: calendar-day delta (always "day" unit) — the helper rounds elapsed ms and would say « dans 5 heures » for a drop later today.
  const when =
    delta === null
      ? ""
      : new Intl.RelativeTimeFormat(i18n.language, { numeric: "auto" }).format(
          delta,
          "day",
        )

  return (
    <section
      className={cn(
        "rounded-xl bg-[#ff5c00] p-4 text-white shadow-sm",
        MOTION_BEATS["drop-reveal"],
        className,
      )}
      data-testid="mission-drop-card"
      aria-label={t("missions.drop.region_label")}
    >
      <p className="text-xs font-bold tracking-wide uppercase opacity-90">
        {t("missions.drop.eyebrow")}
      </p>
      <div className="mt-1 flex items-start gap-2">
        <Clock className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <p className="text-sm font-semibold" data-testid="mission-drop-body">
          {t("missions.drop.body", {
            count: drop.mission_count,
            label: drop.label,
            when,
          })}
        </p>
      </div>
    </section>
  )
}
